import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import FindTutorClassBanner from './FindTutorClassBanner'
import UsePagination from '../../../components/pagination'
import TutorDetails from './TutorDetails'
import FindTutorCalendar from './FindTutorCalendar'
import BookTutor from './BookTutor'

import {
  selectClassBannerLoading,
  selectClassBannerPagination,
  selectClassBannerError,
} from '../../../routing/fetchingfunction/classBannerSearch/classBannerSearch.reducer'
import { searchClassBanners } from '../../../routing/fetchingfunction/classBannerSearch/classBannerSearch.actions'

import getFindTutorFilters from '../../../helpers/getFindTutorFilters'
import { getPaginationTotalLength } from '../../../helpers'
import { Loader } from '../../../components/UI'

// Error message component for search failures
const ErrorMessage = () => (
  <div className="mt-[24px] w-full p-6 rounded-lg bg-red-500 border border-red-600">
    <div className="flex flex-col items-center justify-center text-center">
      <h3 className="text-lg font-semibold text-white mb-2">Unable to load tutor listings</h3>
      <p className="text-white mb-4">
        We're experiencing some technical difficulties. Please try again later.
      </p>
      <button
        className="px-4 py-2 bg-white text-red-600 font-medium rounded-lg hover:bg-gray-100 transition-colors"
        onClick={() => window.location.reload()}
      >
        Retry
      </button>
    </div>
  </div>
)

const FindTutorClassBanners = ({ filters }) => {
  const dispatch = useDispatch()

  const { classBanners, classBannersPage, classBannersTotal, classBannersPageSize } = useSelector(
    selectClassBannerPagination
  )
  const isLoading = useSelector(selectClassBannerLoading)
  const hasError = useSelector(selectClassBannerError)

  const [isTutorDetailsModalOpen, setIsTutorDetailsModalOpen] = useState(false)
  const [classBannerDetails, setClassBannerDetails] = useState(null)
  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false)
  const [classBannerCalendar, setClassBannerCalendar] = useState(null)
  const [classBannerBook, setClassBannerBook] = useState(null)
  const [isClassBannerBookModalOpen, setIsClassBannerBookModalOpen] = useState(false)

  const handleOpenCalendarModal = (classBanner) => {
    setClassBannerCalendar(classBanner)
    setIsCalendarModalOpen(true)
  }

  const handleCloseCalendarModal = () => {
    setIsCalendarModalOpen(false)

    setTimeout(() => {
      setClassBannerCalendar(null)
    }, 300)
  }

  const handleOpenPaymentModal = (selectedSlot) => {
    handleCloseCalendarModal()

    if (classBannerCalendar) {
      setTimeout(() => {
        setClassBannerBook({
          classBanner: classBannerCalendar,
          selectedSlot,
        })
        setIsClassBannerBookModalOpen(true)
      }, 300)
    }
  }

  const handleClosePaymentModal = () => {
    setIsClassBannerBookModalOpen(false)

    setTimeout(() => {
      handleOpenCalendarModal(classBannerBook.classBanner)

      setClassBannerBook(null)
    }, 300)
  }

  const handleHidePaymentModal = () => {
    setIsClassBannerBookModalOpen(false)

    setTimeout(() => {
      setClassBannerBook(null)
    }, 300)
  }

  const handleOpenTutorDetailsModal = (classBanner) => {
    setClassBannerDetails(classBanner)
    setIsTutorDetailsModalOpen(true)
  }

  const handleCloseTutorDetailsModal = () => {
    setIsTutorDetailsModalOpen(false)

    setTimeout(() => {
      setClassBannerDetails(null)
    }, 300)
  }

  const handlePageChange = (_, value) => {
    if (value !== classBannersPage) {
      dispatch(
        searchClassBanners({
          page: value,
          ...getFindTutorFilters(filters),
        })
      )
    }
  }

  useEffect(() => {
    const payload = {
      page: 1,
      ...getFindTutorFilters(filters),
    }

    console.log('API PAYLOAD:', payload)
    console.log('FILTERS SENT:', getFindTutorFilters(filters))
    dispatch(searchClassBanners(payload))
    // dispatch(
    //   searchClassBanners({
    //     page: 1,
    //     ...getFindTutorFilters(filters),
    //   })
    // )
  }, [filters])

  const classBannersContent = classBanners.map((classBanner) => {
    return (
      <FindTutorClassBanner
        key={classBanner.classbanner_hash_1}
        classBanner={classBanner}
        onCalendar={handleOpenCalendarModal}
        onDetails={handleOpenTutorDetailsModal}
      />
    )
  })

  // Show error message when there's an error and not loading
  if (hasError && !isLoading) {
    return <ErrorMessage />
  }

  return (
    <div className="mt-[24px] w-full flex flex-col items-start">
      <div role="status" aria-live="polite">
        <span className="text-lg text-white font-bold">
          {classBannersTotal} tutor{classBannersTotal > 1 ? 's' : ''} found
        </span>
      </div>

      <ul aria-busy={isLoading} className="mt-[24px] w-full flex flex-col items-start gap-y-[16px]">
        {classBanners.length > 0 && classBannersContent}
      </ul>

      {isLoading && (
        <div className="mt-[24px] w-full flex justify-center">
          <Loader className="text-white" />
        </div>
      )}

      {!isLoading && classBanners.length === 0 && !hasError && (
        <div className="mt-[24px] w-full text-center text-white">
          No tutors found matching your criteria.
        </div>
      )}

      {classBannersTotal > classBannersPageSize && (
        <div className="w-full flex items-center justify-center">
          <UsePagination
            count={getPaginationTotalLength(classBannersTotal, classBannersPageSize)}
            page={classBannersPage}
            onChange={handlePageChange}
            loading={isLoading}
            altColors
          />
        </div>
      )}

      <TutorDetails
        open={isTutorDetailsModalOpen}
        classBanner={classBannerDetails}
        onClose={handleCloseTutorDetailsModal}
      />

      <FindTutorCalendar
        open={isCalendarModalOpen}
        classBanner={classBannerCalendar}
        onClose={handleCloseCalendarModal}
        onContinue={handleOpenPaymentModal}
      />

      <BookTutor
        open={isClassBannerBookModalOpen}
        data={classBannerBook}
        onClose={handleClosePaymentModal}
        onHide={handleHidePaymentModal}
      />
    </div>
  )
}

export default FindTutorClassBanners
